
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pencil, Target, MessageSquare, Activity, Repeat, Send, Star, Smile } from 'lucide-react';
import './FeedbackSystem.css';

// ── Service icons (sidebar dropdown)
import iconSmartVitals    from '../../Assets/Images/rewamp/0. feedback form/smart vitals.svg';
import iconSkinCare       from '../../Assets/Images/rewamp/0. feedback form/skin care.svg';
import iconLungCare       from '../../Assets/Images/rewamp/0. feedback form/lungs care.svg';
import iconOralCare       from '../../Assets/Images/rewamp/0. feedback form/oral care.svg';
import iconCareSathi      from '../../Assets/Images/rewamp/0. feedback form/caresathi.svg';
import iconSymptomChecker from '../../Assets/Images/rewamp/0. feedback form/symptom checker.svg';
import iconSmartReport    from '../../Assets/Images/rewamp/0. feedback form/smart report.svg';
import iconSmartDiet      from '../../Assets/Images/rewamp/0. feedback form/smart diet.svg';
import iconOthers         from '../../Assets/Images/rewamp/0. feedback form/others.svg';
import iconCosmeticSkinCare from '../../Assets/Images/rewamp/skin/hover state icon -cosmetic flow.svg';
import iconVisionCare     from '../../Assets/Images/rewamp/0. feedback form/vision care.svg';

// ── Banner images (modal header)
import bannerSmartVitals    from '../../Assets/Images/rewamp/0. feedback form/smart vitals hero image.webp';
import bannerSkinCare       from '../../Assets/Images/rewamp/0. feedback form/skin care banner image.webp';
import bannerLungCare       from '../../Assets/Images/rewamp/0. feedback form/lungscare hero image.webp';
import bannerOralCare       from '../../Assets/Images/rewamp/0. feedback form/oral care hero image.webp';
import bannerCareSathi      from '../../Assets/Images/rewamp/0. feedback form/caresathi hero imahe.webp';
import bannerSymptomChecker from '../../Assets/Images/rewamp/0. feedback form/symptom checker hero image.webp';
import bannerSmartReport    from '../../Assets/Images/rewamp/0. feedback form/smart report hero image.webp';
import bannerSmartDiet      from '../../Assets/Images/rewamp/0. feedback form/smart diet hero image.webp';
import bannerOthers         from '../../Assets/Images/rewamp/0. feedback form/others form hero image.webp';
import bannerVisionCare     from '../../Assets/Images/rewamp/0. feedback form/vision care hero image.webp';

const BASE_URL = process.env.REACT_APP_QUANTUMURL;

const SERVICES = [
  { label: 'SmartVitals',     value: 'Smart Vitals',    iconImg: iconSmartVitals,    desc: 'Know your vitals within seconds' },
  { label: 'SkinCare',        value: 'Skin Care',        iconImg: iconSkinCare,       desc: 'Dermatological AI analysis' },
  { label: 'Cosmetic Skincare', value: 'Skin Care Cosmetic', iconImg: iconCosmeticSkinCare, desc: 'Glow & beauty focused skin analysis' },
  { label: 'LungsCare',       value: 'Lung Care',        iconImg: iconLungCare,       desc: 'Smart lung analysis' },
  { label: 'OralCare',        value: 'Oral Care',        iconImg: iconOralCare,       desc: 'Know your oral health from home' },
  { label: 'VisionCare',      value: 'Vision Care',      iconImg: iconVisionCare,     desc: 'Instant clarity of your eye health' },
  { label: 'CareSathi',       value: 'Care Sathi',       iconImg: iconCareSathi,      desc: 'Routes to all CureBay services' },
  { label: 'SymptomChecker',  value: 'Symptom Checker',  iconImg: iconSymptomChecker, desc: 'Instant answers for everyday symptoms' },
  { label: 'SmartReport',     value: 'Smart Report',     iconImg: iconSmartReport,    desc: 'Clear visuals for smarter health insights' },
  { label: 'SmartDiet',       value: 'Smart Diet',       iconImg: iconSmartDiet,      desc: 'AI-powered nutrition & meal plans' },
  { label: 'Others',          value: 'Other',            iconImg: iconOthers,         desc: 'General feedback or other services' },
];

const SERVICE_BANNERS = {
  'Smart Vitals':    bannerSmartVitals,
  'Skin Care':       bannerSkinCare,
  'Skin Care Cosmetic': bannerSkinCare,
  'Lung Care':       bannerLungCare,
  'Oral Care':       bannerOralCare,
  'Vision Care':     bannerVisionCare,
  'Care Sathi':      bannerCareSathi,
  'Symptom Checker': bannerSymptomChecker,
  'Smart Report':    bannerSmartReport,
  'Smart Diet':      bannerSmartDiet,
  'Other':           bannerOthers,
};

function getServiceHeader(service) {
  const svc = SERVICES.find(s => s.value === service);
  const label = svc?.label || service;
  const displayName = svc?.value || service;
  return {
    title: `Help Us Improve\n${label} Tool`,
    subtitle: `Your feedback helps us make ${displayName} more accurate, personalized, and helpful for you.`,
    formLabel: `${label} Feedback Form`,
    banner: SERVICE_BANNERS[service] || null,
  };
}

// Per-question colors matching Figma — "Question N" pill, category label and icon tile share one hue
const QUESTION_CONFIG = [
  { type: 'EASE OF USE',          Icon: Pencil,        color: '#7C3AED', bg: '#EDE9FE', low: 'Very Difficult', high: 'Very Easy'       },
  { type: 'ACCURACY',             Icon: Target,        color: '#EA580C', bg: '#FFEDD5', low: 'Not Accurate',   high: 'Highly Accurate' },
  { type: 'CLARITY',              Icon: MessageSquare, color: '#2563EB', bg: '#DBEAFE', low: 'Not Clear',      high: 'Very Clear'      },
  { type: 'USEFULNESS',           Icon: Activity,      color: '#16A34A', bg: '#DCFCE7', low: 'Not Useful',     high: 'Very Useful'     },
  { type: 'LIKELIHOOD TO RETURN', Icon: Repeat,        color: '#DB2777', bg: '#FCE7F3', low: 'Very Unlikely',  high: 'Very Likely'     },
];

// Light-blue glowing card look used across the revamp
const GLOW_CARD = { background: '#fff', border: '1px solid #C9EEF8', boxShadow: '0px 4px 10px 0px #80DAF14D' };

const GRAD_TEXT = {
  background: 'var(--primary-gradient)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// Star rating (Figma) — number above each star; outlined grey stars, selected ones
// turn blue with a light-blue fill. Low/high labels sit under the first/last star.
function StarRating({ value, onChange, compact, low, high }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  const size = compact ? 24 : 26;
  const colWidth = compact ? 40 : 46;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
        {[1, 2, 3, 4, 5].map(star => {
          const on = active >= star;
          return (
            <div key={star} style={{ width: colWidth, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: on ? 700 : 500, color: on ? '#1A1A2E' : '#9CA3AF', fontFamily: 'Inter, sans-serif' }}>
                {star}
              </span>
              <button
                type="button"
                onClick={() => onChange(star)}
                onMouseEnter={() => setHover(star)}
                onMouseLeave={() => setHover(0)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}
                aria-label={`Rate ${star} out of 5`}
              >
                <Star
                  size={size}
                  strokeWidth={1.6}
                  color={on ? '#2F80D1' : '#B6BFC9'}
                  fill={on ? '#D7ECFB' : 'transparent'}
                  style={{ transition: 'color 0.12s, fill 0.12s' }}
                />
              </button>
            </div>
          );
        })}
      </div>
      {(low || high) && (
        // Same 5-column grid as the stars: low label centred under star 1, high under star 5
        <div style={{ display: 'flex' }}>
          {[low, null, null, null, high].map((label, idx) => (
            <div key={idx} style={{ width: colWidth, display: 'flex', justifyContent: 'center' }}>
              {label && (
                <span style={{
                  fontSize: compact ? 10 : 11, fontWeight: 500, color: '#8A94A6',
                  fontFamily: 'Inter, sans-serif', whiteSpace: 'nowrap', lineHeight: 1.2,
                }}>
                  {label}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SKIN_CARE_SERVICES = ['Skin Care', 'Skin Care Cosmetic'];

export function FeedbackModal({ service, patientId, onClose, onSubmitted }) {
  const [questions, setQuestions] = useState([]);
  const [ratings, setRatings] = useState({});
  const [openFeedback, setOpenFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(service);
  const isSkinCareToggle = SKIN_CARE_SERVICES.includes(service);
  const isOther = selectedService === 'Other';
  const header = getServiceHeader(selectedService);
  const [title1, title2] = header.title.split('\n');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isOther) { setLoading(false); return; }
    setLoading(true);
    fetch(`${BASE_URL}/feedback/questions?service=${encodeURIComponent(selectedService)}`)
      .then(r => r.json())
      .then(data => { setQuestions(data?.questions || []); setLoading(false); })
      .catch(() => { setLoading(false); setError('Could not load questions.'); });
  }, [selectedService, isOther]);

  const handleSubmit = async () => {
    if (!isOther && questions.some((_, i) => !ratings[i])) {
      setError('Please rate all questions before submitting.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = { patient_id: patientId, order_id: '', service: selectedService, open_feedback: openFeedback || undefined };
      if (!isOther) questions.forEach((q, i) => { body[`rating_${i + 1}`] = ratings[i]; });
      const res = await fetch(`${BASE_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      setTimeout(() => { onSubmitted?.(); onClose(); }, 2000);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '0.5rem' : '1rem',
      }}
    >
      <div className="feedback-no-scroll" style={{
        background: '#fff',
        borderRadius: isMobile ? 12 : 20,
        width: '100%', maxWidth: 780,
        maxHeight: '94vh',
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
        position: 'relative',
      }}>

        {/* ── GRADIENT HEADER ── */}
        <div style={{
          background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)',
          borderRadius: isMobile ? '12px 12px 0 0' : '20px 20px 0 0',
          padding: isMobile ? '16px 0 0 16px' : '28px 0 0 32px',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          minHeight: isMobile ? 130 : 240,
        }}>
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 16, right: 16, zIndex: 2,
              background: 'rgba(255,255,255,0.7)', border: 'none',
              borderRadius: '50%', width: 32, height: 32,
              cursor: 'pointer', fontSize: 15, color: '#555',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700,
            }}
            aria-label="Close"
          >✕</button>

          {/* Left: text content */}
          <div style={{ flex: 1, paddingBottom: isMobile ? 12 : 22, paddingRight: isMobile ? 16 : 0, zIndex: 1 }}>
            {/* "Your Feedback Matters" pill */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#fff',
              border: '1px solid #C9EEF8',
              boxShadow: '0px 2px 8px 0px #80DAF14D',
              borderRadius: 20, padding: '5px 14px 5px 6px',
              marginBottom: 16,
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%', background: '#DFF1FB',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Smile size={14} color="#004276" strokeWidth={2} />
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif', color: 'var(--color-brand-primary)' }}>
                Your Feedback Matters
              </span>
            </div>

            {/* Title — "Help Us Improve" blue, "<Service> Tool" green (Figma) */}
            <div style={{ marginBottom: 10 }}>
              <p style={{ margin: 0, fontFamily: 'Inter, sans-serif', fontSize: isMobile ? 18 : 26, fontWeight: 700, color: 'var(--color-brand-primary)', lineHeight: 1.2 }}>
                {title1}
              </p>
              <p style={{ margin: 0, fontFamily: 'Inter, sans-serif', fontSize: isMobile ? 22 : 32, fontWeight: 700, lineHeight: 1.2, color: 'var(--color-brand-green)' }}>
                {title2}
              </p>
            </div>

            <p style={{ margin: 0, fontFamily: 'Inter, sans-serif', fontSize: isMobile ? 12 : 14, color: '#3A3A3A', lineHeight: 1.5, maxWidth: 360 }}>
              {header.subtitle}
            </p>
          </div>

          {/* Right: banner image */}
          {header.banner && !isMobile && (
            <div style={{ flexShrink: 0, width: 340, alignSelf: 'flex-end', display: 'flex', justifyContent: 'center' }}>
              {/* Cap height so portrait/square banners (Smart Diet, Smart Report) don't stretch the header */}
              <img
                src={header.banner}
                alt={service}
                style={{ maxWidth: '100%', maxHeight: 250, width: 'auto', height: 'auto', display: 'block', objectFit: 'contain' }}
              />
            </div>
          )}
        </div>

        {/* ── WHITE BODY ── */}
        <div style={{ padding: isMobile ? '16px 16px 20px' : '24px 32px 32px', background: '#fff' }}>

          {submitted ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
              <h3 style={{ ...GRAD_TEXT, margin: '0 0 8px', fontSize: 20, fontFamily: 'Inter, sans-serif' }}>Thank you!</h3>
              <p style={{ color: '#6B7280', margin: 0, fontFamily: 'Inter, sans-serif' }}>Your feedback has been submitted successfully.</p>
            </div>
          ) : (
            <>
              {isSkinCareToggle && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  {SKIN_CARE_SERVICES.map((svcValue) => {
                    const active = selectedService === svcValue;
                    return (
                      <button
                        key={svcValue}
                        type="button"
                        onClick={() => setSelectedService(svcValue)}
                        style={{
                          flex: 1, padding: '10px 12px', borderRadius: 10,
                          fontSize: 13, fontWeight: 600, fontFamily: 'Inter, sans-serif',
                          cursor: 'pointer',
                          border: active ? '1.5px solid var(--color-brand-primary)' : '1px solid #E5E7EB',
                          background: active ? 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)' : '#fff',
                          color: active ? 'var(--color-brand-primary)' : '#6B7280',
                        }}
                      >
                        {svcValue === 'Skin Care' ? 'Normal Skincare' : 'Cosmetic Skincare'}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Whole form sits in one glowing card (Figma) */}
              <div style={{ ...GLOW_CARD, borderRadius: 16, padding: isMobile ? '14px 12px 18px' : '18px 20px 22px' }}>

              {/* Form title row */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                paddingBottom: 14, marginBottom: 16,
                borderBottom: '1px solid #DFF1FB',
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: '#DFF1FB',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Star size={15} color="#004276" fill="#004276" />
                </div>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600, color: 'var(--color-brand-primary)' }}>
                  {header.formLabel}
                </span>
              </div>

              {loading && (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF', fontFamily: 'Inter, sans-serif' }}>
                  Loading questions…
                </div>
              )}

              {/* Question cards */}
              {!loading && !isOther && questions.map((q, i) => {
                const cfg = QUESTION_CONFIG[i] || QUESTION_CONFIG[0];
                const { Icon } = cfg;
                const stars = (
                  <StarRating
                    value={ratings[i] || 0}
                    onChange={v => setRatings(r => ({ ...r, [i]: v }))}
                    compact={isMobile}
                    low={cfg.low}
                    high={cfg.high}
                  />
                );
                return (
                  <div key={i} style={{
                    ...GLOW_CARD,
                    borderRadius: 12,
                    padding: isMobile ? '12px' : '14px 18px',
                    marginBottom: 12,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    justifyContent: 'space-between',
                    gap: isMobile ? 10 : 16,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      {/* Icon tile */}
                      <div style={{
                        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                        background: cfg.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon size={16} color={cfg.color} strokeWidth={2} />
                      </div>

                      <div style={{ minWidth: 0 }}>
                        {/* "Question N" pill + category */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                          <span style={{
                            fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 600,
                            background: cfg.bg, color: cfg.color,
                            borderRadius: 6, padding: '2px 8px',
                          }}>Question {i + 1}</span>
                          <span style={{
                            fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 600,
                            color: cfg.color, letterSpacing: 0.4,
                          }}>{cfg.type}</span>
                        </div>

                        {/* Question text */}
                        <p style={{ margin: 0, fontFamily: 'Inter, sans-serif', fontSize: isMobile ? 13 : 14, fontWeight: 600, color: 'var(--color-brand-primary)', lineHeight: 1.35 }}>
                          {q.label || q}
                        </p>
                      </div>
                    </div>

                    {/* side padding leaves room for long end labels ("Highly Accurate") centred under the end stars */}
                    <div style={{ flexShrink: 0, alignSelf: isMobile ? 'center' : 'auto', padding: '0 16px' }}>{stars}</div>
                  </div>
                );
              })}

              {/* Open feedback */}
              <div style={{ marginTop: !isOther ? 18 : 0, paddingTop: !isOther ? 16 : 0, borderTop: !isOther ? '1px solid #DFF1FB' : 'none' }}>
                {/* "Tell us more" header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    background: '#DFF1FB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MessageSquare size={14} color="#004276" strokeWidth={2} />
                  </div>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--color-brand-primary)' }}>
                    Tell us more <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(Optional)</span>
                  </span>
                </div>
                <p style={{ margin: '0 0 8px 38px', fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#6B7280' }}>
                  What did you like most? What can we improve?
                </p>
                <textarea
                  value={openFeedback}
                  onChange={e => setOpenFeedback(e.target.value)}
                  placeholder="Share your thoughts..."
                  rows={3}
                  maxLength={500}
                  style={{
                    width: '100%', resize: 'vertical',
                    border: '1px solid #DFF1FB', borderRadius: 10,
                    padding: '10px 14px', fontSize: 13, color: '#374151',
                    fontFamily: 'Inter, sans-serif', boxSizing: 'border-box',
                    outline: 'none', background: '#F7FBFE',
                    lineHeight: 1.5,
                  }}
                />
                <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF', fontFamily: 'Inter, sans-serif', marginTop: 4 }}>
                  {openFeedback.length}/500
                </div>
              </div>

              {error && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FECACA',
                  borderRadius: 8, padding: '8px 12px',
                  color: '#DC2626', fontSize: 13, fontFamily: 'Inter, sans-serif',
                  marginTop: 12,
                }}>{error}</div>
              )}

              {/* Submit / Skip */}
              <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    width: isMobile ? '100%' : 260, padding: '12px',
                    background: submitting ? '#7FA0BA' : 'var(--color-brand-primary)',
                    color: '#fff', border: 'none', borderRadius: 30,
                    fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: '0px 4px 12px 0px #00427633',
                  }}
                >
                  {!submitting && <Send size={15} strokeWidth={2} />}
                  {submitting ? 'Submitting…' : 'Submit Feedback'}
                </button>
                <button
                  onClick={onClose}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 600,
                    color: 'var(--color-brand-primary)',
                    padding: '4px 8px',
                  }}
                >Skip for Now</button>
              </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── STICKY FEEDBACK BUTTON + DROPDOWN ──────────────────────────────────────
export default function FeedbackSystem({ patientId, externalService, onExternalClose }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const dropdownRef = useRef(null);
  const mobilePanelRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (externalService) setActiveService(externalService);
  }, [externalService]);

  useEffect(() => {
    function handleClickOutside(e) {
      const inSticky = dropdownRef.current?.contains(e.target);
      const inPanel = mobilePanelRef.current?.contains(e.target);
      if (!inSticky && !inPanel) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleServiceClick = useCallback((service) => {
    setDropdownOpen(false);
    setActiveService(service);
  }, []);

  const AI_SCREENING  = SERVICES.slice(0, 4);
  const AI_ASSISTANTS = SERVICES.slice(4);

  // Shared panel body used by both desktop and mobile renderings
  const panelBody = (
    <>
      {/* Header — gradient bg */}
      <div style={{
        padding: '16px 18px 16px',
        background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)',
        borderRadius: '16px 16px 0 0',
        borderBottom: '1px solid #DFF1FB',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%)',
          borderRadius: 20, padding: '4px 12px', marginBottom: 10,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'Inter, sans-serif', ...GRAD_TEXT }}>
            ● Quantum Solutions
          </span>
        </div>
        <h3 style={{
          margin: '0 0 6px',
          fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 700, lineHeight: 1,
          ...GRAD_TEXT,
        }}>Share Your Feedback</h3>
        <p style={{ margin: 0, fontFamily: 'Inter, sans-serif', fontSize: 12, color: 'var(--color-text-subheading)', lineHeight: '18px' }}>
          Select the service you used today and rate your experience
        </p>
      </div>

      {/* AI Screening — white bg */}
      <div style={{ padding: '12px 18px 4px' }}>
        <p style={{ margin: '0 0 8px', fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 600, color: '#94A3B8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
          AI SCREENING TOOLS
        </p>
        {AI_SCREENING.map(svc => (
          <ServiceRow key={svc.value} svc={svc} onClick={handleServiceClick} />
        ))}
      </div>

      {/* AI Assistants — white bg */}
      <div style={{ padding: '10px 18px 4px', borderTop: '1px solid #DFF1FB' }}>
        <p style={{ margin: '0 0 8px', fontFamily: 'Inter, sans-serif', fontSize: 10, fontWeight: 600, color: '#94A3B8', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
          AI ASSISTANTS & TOOLS
        </p>
        {AI_ASSISTANTS.map(svc => (
          <ServiceRow key={svc.value} svc={svc} onClick={handleServiceClick} />
        ))}
      </div>
    </>
  );

  return (
    <>
      {/* Backdrop — blurs/dims page when dropdown is open */}
      {dropdownOpen && (
        <div
          onClick={() => setDropdownOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 999,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
        />
      )}

      <div
        ref={dropdownRef}
        style={{ position: 'fixed', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1000 }}
      >
        {/* Sticky tab — smaller on mobile so it encroaches less into page
            content docked at the same right edge (buttons, steppers, etc.
            across the app were getting visually covered by this at full size). */}
        <button
          onClick={() => setDropdownOpen(o => !o)}
          style={{
            background: 'var(--color-brand-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px 0 0 8px',
            padding: isMobile ? '16px 6px' : '28px 12px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: isMobile ? 6 : 10,
            boxShadow: '0px 4px 10px 0px #80DAF180',
          }}
          aria-label="Share Feedback"
        >
          <span style={{ fontSize: isMobile ? 13 : 16 }}>💬</span>
          <span style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            fontFamily: 'Inter, sans-serif',
            fontSize: isMobile ? 12 : 16,
            fontWeight: 700,
            letterSpacing: '0.02em',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}>
            Share Feedback
          </span>
        </button>

        {/* Desktop dropdown — absolute inside transform context is fine on desktop */}
        {!isMobile && dropdownOpen && (
          <div style={{
            position: 'absolute',
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            width: 340,
            padding: '0 0 10px',
            maxHeight: '88vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }} className="feedback-no-scroll">
            {panelBody}
          </div>
        )}
      </div>

      {/* Mobile panel — rendered outside the transform-ed sticky container so
          position:fixed is truly viewport-relative and never clips off-screen */}
      {isMobile && dropdownOpen && (
        <div
          ref={mobilePanelRef}
          className="feedback-no-scroll"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100vw - 32px)',
            maxWidth: 360,
            maxHeight: '85vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            padding: '0 0 10px',
            zIndex: 1001,
          }}
        >
          {panelBody}
        </div>
      )}

      {activeService && (
        <FeedbackModal
          service={activeService}
          patientId={patientId}
          onClose={() => { setActiveService(null); onExternalClose?.(); }}
          onSubmitted={() => { setActiveService(null); onExternalClose?.(); }}
        />
      )}
    </>
  );
}

function ServiceRow({ svc, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => onClick(svc.value)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', background: hovered ? '#EAF3FA' : 'none',
        border: 'none', borderRadius: 10, padding: '10px 10px',
        cursor: 'pointer', textAlign: 'left',
        display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 2, transition: 'background 0.15s',
      }}
    >
      <img
        src={svc.iconImg}
        alt={svc.label}
        style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: '0 0 2px', fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 600, color: 'var(--color-brand-primary)', lineHeight: 1.2 }}>
          {svc.label}
        </p>
        <p style={{ margin: 0, fontFamily: 'Inter, sans-serif', fontSize: 11, color: '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {svc.desc}
        </p>
      </div>
      <span style={{ color: '#94A3B8', fontSize: 16 }}>›</span>
    </button>
  );
}

// ── HOOKS ──────────────────────────────────────────────────────────────────

export function useServicePageFeedback(patientId, serviceName) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!patientId || !serviceName) return;
    fetch(`${BASE_URL}/feedback/check?patient_id=${encodeURIComponent(patientId)}&service=${encodeURIComponent(serviceName)}`)
      .then(r => r.json())
      .then(data => { if (data?.show_feedback) setShow(true); })
      .catch(() => {});
  }, [patientId, serviceName]);
  return { showFeedback: show, closeFeedback: () => setShow(false) };
}

export function useSolutionFeedback(patientId, setActiveService) {
  const checkAndTrigger = useCallback(async (service) => {
    if (!service || service === 'Other') return;
    try {
      const res = await fetch(
        `${BASE_URL}/feedback/check?patient_id=${encodeURIComponent(patientId)}&service=${encodeURIComponent(service)}`
      );
      const data = await res.json();
      if (data?.show_feedback) setActiveService(service);
    } catch { /* silently fail */ }
  }, [patientId, setActiveService]);
  return { checkAndTrigger };
}